<template lang="pug">
ValidationObserver#schedule-job-step(
  v-slot="{ invalid }"
  tag="div"
  vid="scheduleJobStep"
  slim
)
  AppWizardStep(
    :show-back-button="false"
    :save-and-close="false"
    :save-button-disabled="invalid"
    is-last-step
    :show-delete-button="false"
    @close="$emit('close')"
  )
    table.work-order-info.wow-meta-table.small
      tr(v-if="job.summary")
        td
          strong Job ID
        td {{ job.summary }}
      tr
        td
          strong Work order
        td {{ workOrder.number }}
      tr
        td
          strong Client
        td {{ workOrder.client && workOrder.client.name }}

    .row
      .col-md-12
        ValidationProvider(
          v-slot="{ errors }"
          vid="assignees"
          rules="required"
          slim
        )
          .form-group.job-assignees-group(:class="{ 'has-error': !!errors[0] }")
            .d-flex.align-items-start.justify-content-between
              label.control-label.required(for="assignees")
                | Assignees
                abbr(title="required") *
              a.pull-right.small.select-all(
                href="#"
                @click.prevent="selectAllAssignees"
              )
                | Select all
            AppSelect#job-assignees(
              :value="form.assignees"
              :options="allAssignees"
              :select2-options="assigneesSelect2"
              @input="onSelectClient"
            )
            span.help-block(v-if="errors[0]") {{ errors[0] }}
            span.help-block(v-if="form.errors.assignee_ids")
              span.d-block(
                v-for="(err, index) in form.errors.assignee_ids"
                :key="`${err}-${index}`"
              ) {{ err }}

    ValidationObserver(
      v-slot="{ invalid }"
      vid="job-dates-times"
      slim
    )
      .row.d-sm-flex.align-items-end
        .col-xs-12.col-sm-5
          .form-group.job-dates-group(:class="{ 'has-error': form.errors.date || invalid }")
            .d-flex.align-items-start.justify-content-between
              label.control-label.required(for="job-dates")
                span.mr-10
                  | Date
                  abbr(title="required") *
              span.time-zone-abbr.pull-right(
                v-tooltip="timezone"
                :title="timezone"
              ) {{ timeZoneAbbr }}
            .d-flex.flex-column.flex-sm-row.align-items-md-center.justify-content-start.justify-content-md-between
              .flex-1
                AppDatePicker#job-start-date(
                  v-tooltip
                  data-testid="work-orders-schedule-start-date"
                  :title="disableScheduleTooltip"
                  :value="form.startsAt"
                  required
                  :start-date="form.startsAt"
                  :min-date="minStartDate"
                  :start-week-on="startWeekOn"
                  :type="'daily'"
                  :show-quick-pick="false"
                  :disabled="isJobInThePast"
                  @input="handleDate('startsAt', $event)"
                )
            .hidden
              ValidationProvider(
                name="starts at"
                vid="startsAt"
                rules="required"
                slim
              )
                input(
                  type="hidden"
                  :value="form.startsAt"
                  disabled
                  readonly
                )
              ValidationProvider(
                name="stops at"
                vid="stopsAt"
                rules="required|noSameDatetime:@startsAt"
                slim
              )
                input(
                  type="hidden"
                  :value="form.stopsAt"
                  disabled
                  readonly
                )
        .col-xs-12.col-sm-7.mt-md-0.pl-md-0.mt-10
          .form-group.job-times-group(
            :class="{ 'has-error': invalid || form.errors.start_time || form.errors.stop_time }"
          )
            .d-flex.justify-content-sm-end
              .d-flex.align-items-center.form-group.job-start-time.m-0
                label.label-job-start-time.control-label.required.mb-0.mr-5
                  | From
                  abbr(title="required") *
                AppTimeInput#job-start-time.job-time.flex-grow(
                  v-tooltip
                  :value="startTime"
                  :title="disableScheduleTooltip"
                  show-seconds="never"
                  :disabled="isJobInThePast"
                  @input="handleTimeInput('startsAt', $event)"
                )

              .d-flex.align-items-center.form-group.job-end-time.mx-0.mb-0
                label.label-job-end-time.control-label.required.my-0.ml-0
                  | To
                  abbr(title="required") *
                span.badge.badge-pill.badge-secondary.badge-next-day(v-if="showNextDayPill") Next day
                AppTimeInput#job-end-time.job-time.flex-grow(
                  v-tooltip
                  :value="stopTime"
                  :title="disableScheduleTooltip"
                  :class="{ highlight: hasChanged('endTime') }"
                  show-seconds="never"
                  :disabled="isJobInThePast"
                  @open="toggleTimeBodyClass"
                  @close="toggleTimeBodyClass"
                  @input="handleTimeInput('stopsAt', $event)"
                )

      .p-relative
        #job-date-time-errors.d-flex.flex-column.has-error(v-show="hasApiDateTimeErrors || invalid")
          .d-flex.justify-content-between.flex-wrap(v-if="hasApiDateTimeErrors")
            .flex-1
              span.help-block(v-if="!!apiDateError") {{ apiDateError }}
            .flex-1
              span.help-block(
                v-for="(err, index) in apiTimeErrors"
                :key="`${err}-${index}`"
              ) {{ err }}
          template(v-else-if="!hasApiDateTimeErrors && invalid")
            span.help-block Invalid times

    template(v-if="showRecurringFields")
      ValidationObserver#recurring-settings-fields(
        vid="schedule"
        tag="div"
      )
        div(
          v-tooltip
          :title="disableScheduleTooltip"
        )
          .row.mt-15.d-sm-flex.align-items-end
            .col-xs-12.col-sm-6
              .form-group.mb-sm-0.m-0(:class="{ 'mb-15': isNotOnce }")
                .d-flex.flex-column.align-items-start.justify-content-between
                  label.control-label.required
                    span.d-flex.align-items-center.mr-10
                      | Repeats
                      abbr(title="required") *
                  AppSelect#repeats(
                    :value="repeatType"
                    :disabled="isJobInThePast"
                    :options="repeatOptions"
                    :select2-options="repeatSelect2"
                    @input="onRepeatTypeSelect"
                  )
            template(v-if="!isCustom")
              //- Weekly
              RecurringJobWeeklyInput.col-xs-12.col-sm-6.pl-sm-0(
                v-if="isWeekly"
                :editing="editing"
                :date="form.startsAt"
                :errors="form.errors"
                :value="form.schedule.weekdays"
                @input="onWeeklyInput"
              )
              //- Monthly
              RecurringJobMonthlyInput.col-xs-12.col-sm-6(
                v-else-if="isMonthly"
                :editing="editing"
                :date="form.startsAt"
                :schedule-date="form.schedule.date"
                :schedule="form.schedule"
                :select2-options="select2Options"
                @input="onMonthlyInput"
              )
            //- Custom
            RecurringJobCustomFields.col-xs-12.col-sm-6(
              v-else
              :interval="schedule.interval"
              :repeat-type="schedule.repeat_type"
              :select2-options="select2Options"
              @input="onCustomFieldsInput"
            )

          //- Custom
          .d-block.mt-15(v-if="isCustom")
            //- Weekly
            RecurringJobWeeklyInput#custom-weekly(
              v-if="isWeekly"
              :editing="editing"
              :errors="form.errors"
              :date="form.startsAt"
              :value="form.schedule.weekdays"
              @input="onWeeklyInput"
            )
            //- Monthly
            .row(v-else-if="isMonthly")
              RecurringJobMonthlyInput#custom-monthly.col-xs-12.col-sm-6(
                :editing="editing"
                :date="form.startsAt"
                :schedule-date="form.schedule.date"
                :schedule="form.schedule"
                :select2-options="select2Options"
                @input="onMonthlyInput"
              )

    .row.mt-15
      .col-sm-12
        ValidationProvider(
          v-slot="{ errors }"
          name="notes"
          vid="job-notes"
          rules="max:2000"
          slim
        )
          .form-group.job-notes-group.mb-0(:class="{ 'has-error': !!errors[0] }")
            label.control-label(for="details")
              | Details
            // eslint-disable vue/no-mutating-props
            textarea#job-notes.form-control(
              v-model="form.details"
              rows="5"
            )
            span.help-block(v-if="errors[0]") {{ errors[0] }}
            span.help-block(v-if="form.errors.details")
              template(v-for="err in form.errors.details") {{ err }}

    slot(slot="footer")
      slot
</template>

<script>
/* eslint-disable vue/no-mutating-props */
import { get, cloneDeep, capitalize, isEqual, pick } from 'lodash-es';
import { extend } from 'vee-validate';
import { JOB_REPEAT_TYPE } from '~/app_constants';
import { datetimeWithTz } from '~/utils/work_orders';
import { jobIsRecurrent, defaultSchedule } from '~/utils/recurring_schedule';
import RecurringJobWeeklyInput from '~/components/work_orders/jobs_dialog/RecurringJobWeeklyInput.vue';
import RecurringJobMonthlyInput from '~/components/work_orders/jobs_dialog/RecurringJobMonthlyInput.vue';
import RecurringJobCustomFields from '~/components/work_orders/jobs_dialog/RecurringJobCustomFields.vue';

extend('noSameDatetime', {
  params: ['target'],
  validate(value, { target }) {
    return !value.isSame(target, 'minute');
  },
  message: 'invalid times',
});

const CUSTOM = 'custom';

export const defaultJob = (current, timezone) => {
  const now = timezone ? current.clone().tz(timezone) : current.clone();
  const startsAt = now.clone().hours(9).minutes(0).seconds(0);
  const stopsAt = now.clone().hours(17).minutes(0).seconds(0);

  return {
    assignees: null,
    details: null,
    startsAt,
    stopsAt,
    schedule: defaultSchedule(),
  };
};

export default {
  name: 'ScheduleJobStep',
  components: {
    RecurringJobWeeklyInput,
    RecurringJobMonthlyInput,
    RecurringJobCustomFields,
  },
  // Make this properties available to all the child
  // components of the ScheduleJobStep.
  provide() {
    return {
      editing: this.editing,
      isJobInThePast: this.isJobInThePast,
    };
  },
  props: {
    reset: {
      type: Boolean,
      default: false,
    },
    select2Options: {
      type: Object,
      default: () => ({}),
    },
    startWeekOn: {
      required: true,
    },
    allAssignees: {
      type: Array,
      required: true,
    },
    workOrder: {
      type: Object,
      default: () => null,
    },
    form: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: () => false,
    },
    job: {
      type: Object,
      default: () => ({}),
    },
    timezone: {
      type: String,
      default: () => null,
    },
    timeZoneAbbr: {
      type: String,
      default: () => null,
    },
    isJobInThePast: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    stopTime: moment(),
    startTime: moment(),
    endDateChanged: false,
    endTimeChanged: false,
    // Proxy value where we will store the "repeat" select
    // value. Because `custom` is not a repeatType underneeth,
    // we need to mask this in the UI with this proxy field.
    repeatTypeProxy: JOB_REPEAT_TYPE.Once,
    // Fields to help us track if the progragated changes
    // in schedule are comming from the date field or the
    // actual recurring settings fields.
    changeFromDate: false,
  }),
  computed: {
    apiDateError() {
      const errors = get(this.form.errors, 'date', null);

      return errors && !!errors.length ? errors[0] : null;
    },
    apiTimeErrors() {
      return ['start_time', 'stop_time'].reduce((errors, field) => {
        const messages = get(this.form.errors, field, []);

        if (messages.length) {
          errors.push(...messages.map((msg) => capitalize(msg)));
        }

        return errors;
      }, []);
    },
    hasApiDateTimeErrors() {
      return this.apiTimeErrors.length || !!this.apiDateError;
    },
    disableScheduleTooltip() {
      return !this.isJobInThePast ? '' : 'Schedule cannot be changed';
    },
    currentDate() {
      return this.timezone ? moment().tz(this.timezone) : moment();
    },
    minStartDate() {
      return this.editing && this.job
        ? this.job.startsAt.isAfter(this.currentDate)
          ? this.currentDate
          : this.job.startsAt
        : this.currentDate;
    },
    minEndDate() {
      if (this.form.startsAt.hours() >= 23) {
        return this.form.startsAt.clone().endOf('day').add(1, 'seconds');
      }

      return this.form.startsAt.clone().startOf('day').seconds(0);
    },
    assigneesSelect2() {
      return {
        ...this.select2Options,
        multiple: true,
        placeholder: 'Select assignees',
      };
    },
    repeatSelect2() {
      return {
        ...this.select2Options,
        placeholder: 'Select repeat type',
      };
    },
    hasChanged() {
      return (inputKey) => this[`${inputKey}Changed`];
    },
    showNextDayPill() {
      return this.stopTime.isBefore(this.startTime, 'minute');
    },
    repeatOptions() {
      return [
        // FIXME: We're not allowing converting recurring jobs to one-off jobs
        //        Bernat Jufré 2021-11-26
        ...(this.editing ? [] : [{ id: JOB_REPEAT_TYPE.Once, text: 'Never' }]),
        { id: JOB_REPEAT_TYPE.Weekly, text: 'Weekly' },
        { id: JOB_REPEAT_TYPE.Monthly, text: 'Monthly' },
        { id: CUSTOM, text: 'Custom' },
      ];
    },
    // TODO: Remove this when we're able to handle converting one-off to recurring
    //       Bernat Jufré 2021-11-26
    showRecurringFields() {
      return !this.editing || (this.editing && jobIsRecurrent(this.job));
    },
    schedule() {
      return this.form.schedule || {};
    },
    repeatType() {
      // If we're in custom mode, we need to make sure that we use the
      // proxy in order to not break the UI and also the data model.
      return this.isCustom ? this.repeatTypeProxy : this.schedule.repeat_type;
    },
    isNotOnce() {
      return this.schedule.repeat_type !== JOB_REPEAT_TYPE.Once;
    },
    isWeekly() {
      return this.schedule.repeat_type === JOB_REPEAT_TYPE.Weekly;
    },
    isMonthly() {
      return this.schedule.repeat_type === JOB_REPEAT_TYPE.Monthly;
    },
    isCustom() {
      return this.schedule.custom;
    },
  },
  watch: {
    showNextDayPill(showNextDayPill) {
      if (showNextDayPill) {
        this.form.stopsAt = datetimeWithTz(this.startTime.clone().add(1, 'days'), this.stopTime, this.timezone);
      }
    },
  },
  mounted() {
    !this.editing ? this.mountCreate() : this.mountEdit();
  },
  methods: {
    mountCreate() {
      const now = moment();
      const tzNow = this.timezone ? now.clone().tz(this.timezone) : now.clone();

      this.startTime = tzNow.clone().hours(9).minutes(0).seconds(0);
      this.stopTime = tzNow.clone().hours(17).minutes(0).seconds(0);

      this.checkAndSelectIfOneAssignee();
    },
    async mountEdit() {
      if (this.job) {
        await this.$nextTick();
        const { startsAt, stopsAt } = this.job;

        this.startTime = datetimeWithTz(startsAt, startsAt, this.timezone);
        this.stopTime = datetimeWithTz(startsAt, stopsAt, this.timezone);

        if (this.form.schedule.custom) {
          this.repeatTypeProxy = CUSTOM;
        }
      }
    },
    toggleTimeBodyClass() {
      document.body.classList.toggle('wo-time-right-input');
    },
    async highlightInput(inputKey) {
      const dataKey = `${inputKey}Changed`;

      this[dataKey] = true;
      await this.wait();
      this[dataKey] = false;
    },
    wait(delay = 500) {
      return new Promise((resolve) => setTimeout(resolve, delay));
    },
    async handleDate(which, newDate) {
      const [date, month, year] = [newDate.date(), newDate.month(), newDate.year()];
      const clonnedDate = this.form[which].clone().set({ year, month, date });

      this.form[which] = datetimeWithTz(clonnedDate, clonnedDate, this.timezone);
      this.startTime = datetimeWithTz(this.form.startsAt, this.startTime, this.timezone);

      const clonnedStopsAt = this.form.stopsAt.clone().set({ year, month, date });

      this.form.stopsAt = datetimeWithTz(clonnedStopsAt, clonnedStopsAt, this.timezone);
      this.stopTime = datetimeWithTz(this.form.stopsAt, this.stopTime, this.timezone);

      this.changeFromDate = true;
      // We need to wait for the update to be applied before
      // we can set the flag back to false. Otherwise, the
      // we will be unable to check whether the schedule settings
      // have changed because of the `date` field or not.
      await this.$nextTick();
      this.changeFromDate = false;
    },
    handleTimeInput(which, time) {
      const whichTime = which === 'startsAt' ? 'startTime' : 'stopTime';

      this.form[which] = datetimeWithTz(this.form[which], time, this.timezone);
      // We want to always follow `startTime` as a the date for times
      // as we might have a case where we keep updating the `stopTime`
      // to add 1 day, and we might not be in sync with the feature
      // functionality of job scheduling.
      this[whichTime] = datetimeWithTz(this.startTime, time, this.timezone);

      if (which === 'startsAt') {
        this.handleStartTimeInput();
      }
    },
    handleStartTimeInput() {
      if (this.startTime.isSameOrAfter(this.stopTime)) {
        this.form.stopsAt = this.form.startsAt.clone().add(1, 'hours');
        this.stopTime = this.startTime.clone().add(1, 'hours');

        this.highlightInput('endTime');
      }
    },
    onSelectClient(selected = []) {
      this.form.assignees = selected.map(Number);
    },
    selectAllAssignees() {
      this.form.assignees = (this.allAssignees || []).map((assignee) => assignee.id);
    },
    onRepeatTypeSelect(value) {
      // We only want to set this if we're not already in
      // "custom mode", as to not break the UI and data model.
      if (!this.isCustom) {
        this.repeatTypeProxy = value;
      }

      // Reset the schedule so we can handle changes properly
      let schedule = defaultSchedule();

      if (value === CUSTOM) {
        schedule.custom = true;
        schedule.repeat_type = JOB_REPEAT_TYPE.Weekly;

        if (this.editing) {
          // eslint-disable-next-line no-unused-vars
          const { custom, ...rest } = cloneDeep(this.job.schedule || {});

          // If we're editing, do our best to show the correct fields
          // instead of making the user do so.
          // i.e: When it's monthly, they change to custom, we don't wanna
          // show the weekly options but rather the monthly ones.
          schedule = { ...schedule, ...rest };
        }
      } else {
        if (this.editing) {
          // If we're moving from another recurring type to the original one, make sure we
          // showcase the right settings.
          if (this.job.schedule.repeat_type === value) {
            schedule = { ...schedule, ...cloneDeep(this.job.schedule || {}) };
          }
          // Make sure we keep the `schedule.date` field intact
          schedule.date = this.job.schedule.date;
        }
        schedule.custom = false;
        schedule.repeat_type = value;
      }

      // Here we don't need to check against the `changeFromDate` because the `repeat_type`
      // field has no dependency with `date` field.
      // If we're editing, we check if the `repeat_type` has changed
      const hasChangedSchedule = this.editing && this.job.schedule.repeat_type !== schedule.repeat_type;

      this.form.schedule = schedule;
      this.form.userHasChangedSchedule = hasChangedSchedule;
    },
    onWeeklyInput(newWeekdays = []) {
      // Set whether the user has triggered the change or not.
      //
      // 1. First if we're not in editing mode, we don't care about this flag.
      // 2. If the check is comming because the `date` field has changed
      //    We don't want to continue with the check and we return `false`
      // 3. If it's not the case, we check if the `weekdays` field has changed
      const hasChangedSchedule =
        this.editing && !this.changeFromDate && !isEqual(this.job.schedule.weekdays, newWeekdays);

      this.form.schedule.weekdays = newWeekdays.sort();
      this.form.userHasChangedSchedule = hasChangedSchedule;
    },
    onMonthlyInput(newMonthlySettings) {
      const monthlyKeys = Object.keys(newMonthlySettings);
      const { monthday, monthweekday, monthweekdaynum } = newMonthlySettings;
      // Set whether the user has triggered the change or not.
      //
      // 1. First if we're not in editing mode, we don't care about this flag.
      // 2. If the check is comming because the `date` field has changed
      //    We don't want to continue with the check and we return `false`
      // 3. If it's not the case, we check if the any of the monthly fields
      //    (monthday, monthweekday, monthweekdaynum) have changed
      const hasChangedSchedule =
        this.editing && !this.changeFromDate && !isEqual(pick(this.job.schedule, monthlyKeys), newMonthlySettings);

      this.form.schedule.monthday = monthday;
      this.form.schedule.monthweekday = monthweekday;
      this.form.schedule.monthweekdaynum = monthweekdaynum;
      this.form.userHasChangedSchedule = hasChangedSchedule;
    },
    onCustomFieldsInput({ interval, repeatType }) {
      if (repeatType !== this.job.schedule.repeat_type) {
        this.form.schedule = { ...defaultSchedule(), custom: true };

        if (this.editing) {
          // If we're moving from another recurring type to the original one, make sure we
          // showcase the right settings.
          if (this.job.schedule.repeat_type === repeatType) {
            this.form.schedule = { ...defaultSchedule(), ...cloneDeep(this.job.schedule || {}), custom: true };
          }
          // Make sure we keep the `schedule.date` field intact
          this.form.schedule.date = this.job.schedule.date;
        }
      }

      // Set whether the user has triggered the change or not.
      //
      // 1. First if we're not in editing mode, we don't care about this flag.
      // 2. If the check is comming because the `date` field has changed
      //    We don't want to continue with the check and we return `false`
      // 3. If it's not the case, we check if the either the `interval` or the `repeat_type` have changed
      const hasChangedSchedule =
        this.editing &&
        !this.changeFromDate &&
        this.job.schedule.interval !== interval &&
        this.job.schedule.repeat_type !== repeatType;

      this.form.schedule.interval = interval;
      this.form.schedule.repeat_type = repeatType;
      this.form.userHasChangedSchedule = hasChangedSchedule;
    },
    async checkAndSelectIfOneAssignee() {
      if (this.allAssignees && this.allAssignees.length === 1) {
        // This awaiting for next tick is necessary due to the latest
        // changes in the `AppSelect` component where we added a summary feature.
        //    See here https://github.com/NetsoftHoldings/hubstaff-server/commit/fd90ee1874b0687e1422364b556508f9987d5a66)
        // Because the underlying component depends on jQuery and it's
        // watching the "value", triggering this causes the JS to crash.
        await this.$nextTick();
        this.selectAllAssignees();
      }
    },
  },
};
/* eslint-enable vue/no-mutating-props */
</script>
